import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./config/supabase";
import {
  sanitizeError,
  isValidPassword,
  getPasswordValidationError,
} from "./utils/security";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [canReset, setCanReset] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [otpCooldown, setOtpCooldown] = useState<number>(0);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const isRecoveryLink = useMemo(() => {
    try {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      return hash.includes("type=recovery") || search.includes("type=recovery");
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const tryEstablishSession = async () => {
      try {
        const hash = window.location.hash || "";
        const search = new URLSearchParams(window.location.search || "");

        // Case 1: PKCE/code flow (?code=...&type=recovery)
        const code = search.get("code");
        const typeSearch = search.get("type");
        if (code && typeSearch === "recovery") {
          try {
            // Exchange the full URL for a session (v2 parses code internally)
            const { error } = await supabase.auth.exchangeCodeForSession(
              window.location.href
            );
            if (!error) {
              setCanReset(true);
              return;
            }
          } catch {}
        }

        // Case 2: Hash tokens (#access_token=...&refresh_token=...&type=recovery)
        if (
          hash.includes("type=recovery") &&
          hash.includes("access_token") &&
          hash.includes("refresh_token")
        ) {
          const params = new URLSearchParams(hash.replace(/^#/, ""));
          const access_token = params.get("access_token") || "";
          const refresh_token = params.get("refresh_token") || "";
          if (access_token && refresh_token) {
            const { error } = await supabase.auth.setSession({
              access_token,
              refresh_token,
            });
            if (!error) {
              setCanReset(true);
              return;
            }
          }
        }

        // Fallback: rely on auth state change
        if (isRecoveryLink) {
          setCanReset(true);
        }

        // Also, if we already have a valid session (e.g., OTP verified elsewhere), allow reset
        const { data: sess } = await supabase.auth.getSession();
        if (sess?.session?.user) {
          setCanReset(true);
        }
      } catch {
        // ignore
      }
    };

    tryEstablishSession();

    // Also listen for Supabase auth state to catch PASSWORD_RECOVERY event
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setCanReset(true);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [isRecoveryLink]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("");
    if (!email || !otp) {
      setStatus("Please enter your email and the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otp.trim(),
        type: "recovery",
      });
      if (error) throw error;
      setCanReset(true);
      setStatus("Code verified. You can now set a new password.");
    } catch (err: any) {
      const safe = sanitizeError(err);
      setStatus(`Failed to verify code: ${safe}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setStatus("");
    if (otpCooldown > 0) return;
    if (!email) {
      setStatus("Please enter your email to resend the code.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        {
          redirectTo: `${window.location.origin}/reset-password#provider=recovery`,
        }
      );
      if (error) throw error;
      setStatus("A new recovery code has been sent to your email.");
      setOtp("");
      setOtpCooldown(60);
    } catch (err: any) {
      const safe = sanitizeError(err);
      setStatus(`Failed to resend code: ${safe}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => {
      setOtpCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("");
    if (!canReset) {
      setStatus(
        "Invalid or expired password reset link. Please request a new one from the login page."
      );
      return;
    }

    if (!isValidPassword(password)) {
      const passwordError = getPasswordValidationError(password);
      setStatus(passwordError || "Password does not meet requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setStatus("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setStatus("Password updated successfully. Redirecting to login...");
      // Clear recovery session and go to login
      setTimeout(async () => {
        try {
          await supabase.auth.signOut();
        } catch {}
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err: any) {
      const safe = sanitizeError(err);
      setStatus(`Failed to update password: ${safe}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center py-6 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="text-center mb-6">
          <a href="/" className="inline-block mb-4">
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10 mx-auto"
            />
          </a>
          <h1 className="text-2xl font-extrabold text-brand-dark-blue">
            Reset your password
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Enter the recovery code we emailed you, then set a new password.
          </p>
        </div>
        {/* OTP verify form (before session established) */}
        {!canReset && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                6-digit recovery code
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                placeholder="Enter code"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading || otpCooldown > 0 || !email}
              className="w-full bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {otpCooldown > 0
                ? `Resend code in ${otpCooldown}s`
                : "Resend code"}
            </button>

            {status && (
              <div
                className={`rounded-lg mt-3 px-4 py-3 text-center ${
                  status.toLowerCase().includes("verified")
                    ? "bg-green-50 text-green-700 border border-green-200 text-lg font-semibold"
                    : "bg-yellow-50 text-yellow-800 border border-yellow-200 text-sm font-medium"
                }`}
              >
                {status}
              </div>
            )}
          </form>
        )}

        {/* Password update form (after verification) */}
        {canReset && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                New password
              </label>
              <input
                id="new-password"
                type="password"
                maxLength={128}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                maxLength={128}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                placeholder="Re-enter new password"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !canReset}
              className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>

            {status && (
              <div
                className={`p-3 rounded-lg text-sm font-medium mt-2 ${
                  status.toLowerCase().includes("success")
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-yellow-50 text-yellow-800 border border-yellow-200"
                }`}
              >
                {status}
              </div>
            )}
          </form>
        )}

        <div className="text-center mt-2">
          <a
            href="/login"
            className="text-sm text-brand-cyan hover:text-brand-dark-blue underline"
          >
            Back to login
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
